import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { User, LoginCredentials } from '../types';
import { authService } from '../services/authService';
import { toast } from 'sonner';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => void;
  checkAuth: () => Promise<void>;
  validateAuth: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export { AuthContext };

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const userRef = useRef<User | null>(null);
  const lastValidationRef = useRef<number>(0);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  const validateAuth = async (): Promise<boolean> => {
    try {
      console.log('[AuthContext] Validando estado de autenticação...');
      const validation = await authService.validateAuthState();
      
      if (!validation.valid) {
        console.log('[AuthContext] Autenticação inválida:', validation.error);
        setUser(null);
        authService.logout();
        
        if (validation.error?.includes('expirado')) {
          toast.error('Sessão expirada. Faça login novamente.');
        } else if (validation.error?.includes('inconsistência')) {
          toast.error('Inconsistência de autenticação detectada. Faça login novamente.');
        } else {
          toast.error('Erro de autenticação. Faça login novamente.');
        }
        
        return false;
      }
      
      if (validation.user) {
        console.log('[AuthContext] Autenticação válida, usuário:', validation.user.email);
        const current = userRef.current;
        if (!current || current.id !== validation.user.id || current.email !== validation.user.email) {
          setUser(validation.user);
        }
        lastValidationRef.current = Date.now();
      }
      
      return true;
    } catch (error) {
      console.error('[AuthContext] Erro na validação:', error);
      setUser(null);
      authService.logout();
      toast.error('Erro ao validar autenticação. Faça login novamente.');
      return false;
    }
  };

  const checkAuth = async () => {
    console.log('[AuthContext] Verificando autenticação...');
    
    const token = authService.getStoredToken();
    const storedUser = authService.getStoredUser();

    if (!token || !storedUser) {
      console.log('[AuthContext] Token ou usuário não encontrados');
      setLoading(false);
      return;
    }

    // Validar estado completo
    const isValid = await validateAuth();
    setLoading(false);
    
    if (isValid) {
      console.log('[AuthContext] Autenticação verificada com sucesso');
    }
  };

  useEffect(() => {
    checkAuth();
    
    // Configurar validação periódica (a cada 5 minutos)
    const interval = setInterval(validateAuth, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, []);

  // Validar quando a aba ganha foco (usuário voltou para a página) — com cooldown de 60s
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && userRef.current) {
        const elapsed = Date.now() - lastValidationRef.current;
        if (elapsed >= 60_000) {
          validateAuth();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Detectar login de outro usuário em outra aba (token trocado via localStorage)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'prainte_token' && e.newValue !== e.oldValue) {
        toast.error('Sessão alterada em outra aba. Faça login novamente.');
        authService.logout();
        setUser(null);
        window.location.href = '/login';
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const login = async (credentials: LoginCredentials) => {
    try {
      console.log('[AuthContext.login] Iniciando login');
      const { access_token } = await authService.login(credentials);
      console.log('[AuthContext.login] Token recebido');
      
      const currentUser = await authService.getCurrentUser();
      console.log('[AuthContext.login] Dados do usuário obtidos:', currentUser.email);
      
      setUser(currentUser);
      console.log('[AuthContext.login] Login completo, usuário:', currentUser.email, 'org:', currentUser.organization_id);
      
      toast.success(`Bem-vindo, ${currentUser.username || currentUser.email}!`);
    } catch (error: any) {
      console.error('[AuthContext] Erro no login:', error);
      const errorMsg = error.message || 'Erro ao fazer login. Verifique suas credenciais.';
      console.error('[AuthContext] Mensagem de erro:', errorMsg);
      throw error;
    }
  };

  const logout = () => {
    console.log('[AuthContext.logout] Fazendo logout do usuário:', user?.email);
    authService.logout();
    setUser(null);
    toast.success('Logout realizado com sucesso!');
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        loading,
        login,
        logout,
        checkAuth,
        validateAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
